// Self-building table of contents (paired with blocks/toc-nav.liquid).
// Scans its scope for headings (data-levels, default "h3, h4"), assigns slug
// ids to headings that lack one, renders the link list, and scroll-spies the
// active heading. Scope = [data-toc-content] inside the closest Shopify
// section, falling back to the section wrapper itself.
// Label = the heading's data-toc-label attribute or its text content;
// headings inside [data-toc-ignore] are skipped. Anchor motion comes from the
// global html { scroll-behavior } + [id] { scroll-margin-top } rules, so the
// reduced-motion gate in tailwind.input.css covers link jumps too.

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'toc';

class TocNav extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onClick = this._onClick.bind(this);
    this._headings = [];
    this._links = new Map();
    this._active = null;
    this._ticking = false;
    this._held = false;
    this._holdTimer = 0;
  }

  connectedCallback() {
    try {
      this._list = this.querySelector('[data-toc-list]');
      this._toggle = this.querySelector('[data-toc-toggle]');
      this._panel = this.querySelector('[data-toc-panel]');
      this._indicator = this.querySelector('[data-toc-indicator]');
      this._build();
      if (!this._headings.length) {
        // Misconfigured scope (no headings to index) — an empty rail/dropdown
        // is worse than none.
        this.hidden = true;
        return;
      }
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this.addEventListener('click', this._onClick);
      this._updateActive();
    } catch (_) {}
  }

  disconnectedCallback() {
    try {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      this.removeEventListener('click', this._onClick);
      clearTimeout(this._holdTimer);
    } catch (_) {}
  }

  _scope() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('[data-toc-content]') || section;
  }

  _build() {
    if (!this._list) return;
    const selector = this.getAttribute('data-levels') || 'h3, h4';
    const found = [];
    this._scope()
      .querySelectorAll(selector)
      .forEach((el) => {
        if (this.contains(el) || el.closest('[data-toc-ignore]')) return;
        const label = (el.getAttribute('data-toc-label') || el.textContent || '').trim();
        if (!label) return;
        if (!el.id) {
          const base = slugify(label);
          let id = base;
          let n = 2;
          while (document.getElementById(id)) id = base + '-' + n++;
          el.id = id;
        }
        found.push({ el, label, level: parseInt(el.tagName.slice(1), 10) || 6 });
      });
    if (!found.length) return;

    const topLevel = Math.min(...found.map((h) => h.level));
    this._list.textContent = '';
    found.forEach((h) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.el.id;
      // Label goes in a span, duplicated into data-text: the block CSS stacks
      // a hidden bold twin under the text so activating never rewraps lines.
      const span = document.createElement('span');
      span.textContent = h.label;
      a.appendChild(span);
      a.setAttribute('data-text', h.label);
      a.className = h.level > topLevel ? 'toc-nav__link toc-nav__link--sub' : 'toc-nav__link';
      li.appendChild(a);
      this._list.appendChild(li);
      this._links.set(h.el, a);
    });
    this._headings = found.map((h) => h.el);
  }

  // Active = the last heading at or above the anchor line (the global
  // scroll-margin-top), so the highlight matches where a link jump lands.
  _updateActive() {
    let offset = 152;
    try {
      offset = parseFloat(getComputedStyle(this._headings[0]).scrollMarginTop) || offset;
    } catch (_) {}
    let current = this._headings[0];
    for (const el of this._headings) {
      if (el.getBoundingClientRect().top <= offset + 8) current = el;
      else break;
    }
    // At the very bottom of the page the last heading may never reach the
    // anchor line (not enough content below it) — force it active there.
    const doc = document.documentElement;
    if (window.innerHeight + window.scrollY >= doc.scrollHeight - 2) {
      current = this._headings[this._headings.length - 1];
    }
    this._setActive(current);
  }

  _setActive(el) {
    if (el === this._active) return;
    const prev = this._links.get(this._active);
    if (prev) prev.removeAttribute('data-active');
    const next = this._links.get(el);
    if (next) next.setAttribute('data-active', '');
    this._active = el;
    this._positionIndicator();
  }

  // Slide the rail pill to the active link. translateY is tweened by CSS;
  // height snaps (layout properties don't transition — motion.md).
  _positionIndicator() {
    const bar = this._indicator;
    if (!bar) return;
    const a = this._links.get(this._active);
    if (!a) {
      bar.hidden = true;
      return;
    }
    const cs = getComputedStyle(a);
    const inset = parseFloat(cs.paddingTop) || 0;
    const top = a.offsetTop + inset;
    const height = a.offsetHeight - inset - (parseFloat(cs.paddingBottom) || 0);
    if (bar.hidden) {
      // First reveal: place it without a slide-in from the rail top.
      bar.style.transition = 'none';
      bar.hidden = false;
      requestAnimationFrame(() => {
        bar.style.transition = '';
      });
    }
    bar.style.height = height + 'px';
    bar.style.transform = 'translateY(' + top + 'px)';
  }

  // While a clicked link's smooth scroll settles, hold its highlight instead
  // of spying through every heading the scroll passes. Each scroll event
  // extends the hold; 200ms of quiet releases it and re-syncs.
  _holdActive() {
    this._held = true;
    clearTimeout(this._holdTimer);
    this._holdTimer = setTimeout(() => {
      this._held = false;
      this._updateActive();
    }, 200);
  }

  _setOpen(open) {
    if (this._toggle) this._toggle.setAttribute('aria-expanded', String(!!open));
    if (this._panel) {
      this._panel.classList.remove('grid-rows-[0fr]', 'grid-rows-[1fr]');
      this._panel.classList.add(open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]');
    }
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._ticking = false;
      if (this._held) {
        this._holdActive();
        return;
      }
      this._updateActive();
      this._positionIndicator();
    });
  }

  _onClick(ev) {
    try {
      const toggle = ev.target.closest('[data-toc-toggle]');
      if (toggle && this.contains(toggle)) {
        this._setOpen(toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const link = ev.target.closest('.toc-nav__link');
      if (link && this.contains(link)) {
        // Snap the mobile dropdown closed with no transition BEFORE the
        // browser computes the anchor's scroll target — an animated collapse
        // keeps shrinking the document above the target mid-scroll, so the
        // jump lands a panel-height short. At lg the toggle is display:none
        // and the panel is CSS-forced open; nothing to close.
        if (this._toggle && this._toggle.offsetParent !== null && this._panel) {
          this._panel.style.transition = 'none';
          this._setOpen(false);
          void this._panel.offsetHeight;
          requestAnimationFrame(() => {
            this._panel.style.transition = '';
          });
        }
        for (const [el, a] of this._links) {
          if (a === link) {
            this._setActive(el);
            this._holdActive();
            break;
          }
        }
      }
    } catch (_) {}
  }
}

try {
  const TAG = 'toc-nav';
  if (!customElements.get(TAG)) customElements.define(TAG, TocNav);
} catch (_) {}
